import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";

// Components
import RecipeFilters from "src/components/molecules/recipeFilters/RecipeFilters";
import RecipeList from "src/components/organisms/recipeList/RecipeList";

// Types
import { RecipeFieldTypes, TypeTagBase } from "src/types/contentfulTypes";

// State
import { usePinnedRecipes } from "src/context/PinnedRecipesContext";
// import { usePinnedRecipesState } from "src/hooks/usePinnedRecipes";
import { useFilteredTags } from "src/hooks/useFilteredTags";
import { useRecipes } from "src/hooks/useRecipes";
import { filterRecipesByPinnedOption } from "./filterRecipesHelper";

const ListPage = () => {
  const location = useLocation();
  const { pinnedRecipes: storedPinnedRecipes } = usePinnedRecipes();

  // const { pinnedRecipes: storedPinnedRecipeSlugs } = usePinnedRecipesState();
  const { filteredTags, setFilteredTags } = useFilteredTags();
  const { data: allRecipes }: { data: any } = useRecipes();
  const [searchInputValue, setSearchInputValue] = useState<string | null>(null);

  useEffect(() => {
    // Home-page filters are ephemeral unless a recipe page explicitly links back
    // with a tag in router state.
    const navigationState = location.state as {
      filteredTags?: TypeTagBase[];
    } | null;

    setFilteredTags(navigationState?.filteredTags ?? []);
    setSearchInputValue(null);
  }, [location.key, location.state, setFilteredTags]);

  const { pinnedRecipes, nonPinnedRecipes } = useMemo(() => {
    if (!allRecipes?.length) {
      return {
        pinnedRecipes: [] as RecipeFieldTypes[],
        nonPinnedRecipes: [] as RecipeFieldTypes[],
      };
    }

    // Reuse the multiselect's text input as a lightweight recipe-name search.
    const normalizedSearch = searchInputValue?.trim().toLowerCase() ?? "";
    const recipesMatchingSearch = normalizedSearch
      ? allRecipes.filter((recipe: RecipeFieldTypes) =>
          recipe.name.toLowerCase().includes(normalizedSearch)
        )
      : allRecipes;

    const getRecipesByPinnedState = (filterByPinnedRecipes: boolean) =>
      filterRecipesByPinnedOption({
        recipes: recipesMatchingSearch,
        pinnedRecipeSlugs: storedPinnedRecipes,
        filterByTags: filteredTags,
        filterByPinnedRecipes,
      });

    return {
      pinnedRecipes: getRecipesByPinnedState(true),
      nonPinnedRecipes: getRecipesByPinnedState(false),
    };
  }, [allRecipes, filteredTags, searchInputValue, storedPinnedRecipes]);

  useEffect(() => {
    if (!allRecipes?.length || nonPinnedRecipes.length) {
      return;
    }

    ReactGA.event({
      category: "Filters",
      action: "No recipes found",
      label: filteredTags.length ? "tag_filters" : "no_matching_recipes",
    });
  }, [allRecipes, filteredTags.length, nonPinnedRecipes.length]);

  return (
    <div className="container container--page">
      <Helmet>
        <title>Recipe Finder</title>
        <meta
          name="description"
          content="Browse, pin, and filter favourite recipes."
        />
      </Helmet>

      <h1>Find a recipe</h1>

      <RecipeFilters
        // Force a fresh multiselect instance when revisiting the route so its
        // internal input state does not leak across browser history entries.
        key={location.key}
        filteredTags={filteredTags}
        searchInputValue={searchInputValue}
        setSearchInputValue={setSearchInputValue}
      />

      {pinnedRecipes.length ? (
        <>
          <h2>
            {filteredTags.length ? "Filtered pinned recipes" : "Pinned recipes"}
          </h2>
          <RecipeList recipes={pinnedRecipes} />
        </>
      ) : (
        ""
      )}

      <h2>{filteredTags.length ? "Filtered recipes" : "Recipes"}</h2>

      {nonPinnedRecipes.length ? (
        <RecipeList recipes={nonPinnedRecipes} />
      ) : (
        <div>No recipes found</div>
      )}
    </div>
  );
};

export default ListPage;
